import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { ConnectorInvocationStatus } from '@/types/core/connector-invocation-status';
import { useGracePeriod, usePollingRefetch } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { connectorQueryKeys } from './query-keys';
import { useFindConnectorFeatureInvocationStatus } from './use-find-connector-feature-invocation-status';

const REQUESTED_RUN_POLL_DELAY_MS = 1000;
const SLOW_POLL_DELAY_MS = 5_000;
const RUN_SEQUENCE_GRACE_PERIOD_MS = 3000;
const POLL_RAMP_FACTOR = 1.5;
const POLL_RAMP_MAX_DELAY_MS = 15_000;

const ACTIVE_INVOCATION_STATUSES = new Set([
  'queued',
  'requested',
  'running',
  'reconciling',
  'waiting',
  'updating',
]);

// Statuses that change rapidly — poll fast with a ramp.
const FAST_POLL_STATUSES = new Set([
  'running',
  'reconciling',
  'waiting',
  'updating',
]);

// Statuses that change slowly — poll at the slow heartbeat rate.
const SLOW_POLL_STATUSES = new Set([
  'queued',
  'requested',
  'completed',
  'failed',
  'stopped',
]);

export type ConnectorFeatureRunStatusResult = {
  status: ConnectorInvocationStatus | null | undefined;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  /** Call after a manual play action to immediately switch to fast polling. */
  kickFastPoll: () => void;
};

export function useConnectorFeatureRunStatus(
  tenantId: UUID | undefined,
  connectorId: UUID | undefined,
  featureId: UUID | undefined,
  runSequence: number | undefined,
  opts?: { isFeatureEnabled?: boolean }
): ConnectorFeatureRunStatusResult {
  const featureEnabled = opts?.isFeatureEnabled !== false;
  const query = useFindConnectorFeatureInvocationStatus(
    tenantId,
    connectorId,
    featureId,
    runSequence
  );

  const status = query.data;

  // Temporarily force fast polling after a manual play action, so the UI
  // doesn't wait 5 s to see the run transition out of queued/requested.
  const [forcedFastPoll, setForcedFastPoll] = useState(false);
  const forcedFastPollTimerRef = useRef<number | null>(null);

  const kickFastPoll = useCallback(() => {
    setForcedFastPoll(true);
    if (forcedFastPollTimerRef.current !== null) {
      window.clearTimeout(forcedFastPollTimerRef.current);
    }
    // Cancel the forced window once the run is actually running (natural fast
    // poll takes over) or after 30 s as a safety valve.
    forcedFastPollTimerRef.current = window.setTimeout(() => {
      setForcedFastPoll(false);
    }, 30_000);
  }, []);

  // Cancel forced fast poll as soon as the status enters a naturally fast-
  // polled phase or lands on a terminal state — no need to keep forcing it.
  useEffect(() => {
    const curr = status?.status;
    if (!curr) return;
    if (FAST_POLL_STATUSES.has(curr) || !ACTIVE_INVOCATION_STATUSES.has(curr)) {
      if (forcedFastPollTimerRef.current !== null) {
        window.clearTimeout(forcedFastPollTimerRef.current);
        forcedFastPollTimerRef.current = null;
      }
      setForcedFastPoll(false);
    }
  }, [status?.status]);

  // Grace period: suppress the "no status" state for a short window after a
  // runSequence first appears, because the read-model projection may not be
  // ready yet and the API can return 404 transiently.
  const isGracePeriod = useGracePeriod(
    runSequence,
    RUN_SEQUENCE_GRACE_PERIOD_MS
  );

  // Fast poll (1 s with ramp) for statuses that transition quickly, or when
  // the user just pressed play (forcedFastPoll).
  const shouldFastPoll =
    !query.isError &&
    !query.isFetching &&
    runSequence !== undefined &&
    (forcedFastPoll ||
      FAST_POLL_STATUSES.has(status?.status ?? '') ||
      (!status && featureEnabled));

  usePollingRefetch(
    shouldFastPoll,
    () => void query.refetch(),
    REQUESTED_RUN_POLL_DELAY_MS,
    { factor: POLL_RAMP_FACTOR, maxDelayMs: POLL_RAMP_MAX_DELAY_MS },
    status?.status
  );

  // Slow poll (5 s, no ramp) for queued and terminal states. The backend
  // auto-queues the next run after completion, so terminal is never truly
  // final and queued runs transition to requested on their own schedule.
  const shouldSlowPoll =
    !forcedFastPoll &&
    !query.isError &&
    !query.isFetching &&
    runSequence !== undefined &&
    SLOW_POLL_STATUSES.has(status?.status ?? '');

  usePollingRefetch(
    shouldSlowPoll,
    () => void query.refetch(),
    SLOW_POLL_DELAY_MS
  );

  // When status changes between active phases, fire an immediate refetch so
  // the UI reflects the transition without waiting for the next ramp tick.
  const prevActiveStatusRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevActiveStatusRef.current;
    const curr = status?.status;
    prevActiveStatusRef.current = curr;
    const bothActive =
      prev !== undefined &&
      ACTIVE_INVOCATION_STATUSES.has(prev ?? '') &&
      curr !== undefined &&
      ACTIVE_INVOCATION_STATUSES.has(curr ?? '');
    if (bothActive && prev !== curr) {
      void query.refetch();
    }
  }, [status?.status, query]);

  // Invalidate the paginated history list whenever the status changes so it
  // stays consistent with the live status. This covers both active → terminal
  // transitions and terminal → queued (auto-queued next run by the backend).
  const qc = useQueryClient();
  const prevStatusRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevStatusRef.current;
    const curr = status?.status;
    prevStatusRef.current = curr;
    if (prev === undefined || prev === curr) return;
    qc.invalidateQueries({
      queryKey: connectorQueryKeys.invocationStatusesByFeature(
        tenantId,
        connectorId,
        featureId
      ),
      refetchType: 'all',
    });
  }, [status?.status, qc, tenantId, connectorId, featureId]);

  return {
    status,
    isLoading: query.isLoading || (isGracePeriod && !status),
    isError: query.isError,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => void query.refetch(),
    kickFastPoll,
  };
}
